/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertIsInstructionWithAccounts,
  containsBytes,
  fixEncoderSize,
  getBytesEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__FAILED_TO_IDENTIFY_ACCOUNT,
  SOLANA_ERROR__PROGRAM_CLIENTS__FAILED_TO_IDENTIFY_INSTRUCTION,
  SOLANA_ERROR__PROGRAM_CLIENTS__UNRECOGNIZED_INSTRUCTION_TYPE,
  SolanaError,
  type Address,
  type ClientWithPayer,
  type ClientWithRpc,
  type ClientWithTransactionPlanning,
  type ClientWithTransactionSending,
  type GetAccountInfoApi,
  type GetMultipleAccountsApi,
  type Instruction,
  type InstructionWithData,
  type ReadonlyUint8Array,
} from '@solana/kit';
import {
  addSelfFetchFunctions,
  addSelfPlanAndSendFunctions,
  type SelfFetchFunctions,
  type SelfPlanAndSendFunctions,
} from '@solana/program-client-core';
import {
  getAmmConfigCodec,
  getCpmmMigratorStateCodec,
  getInitConfigCodec,
  getLaunchCodec,
  type AmmConfig,
  type AmmConfigArgs,
  type CpmmMigratorState,
  type CpmmMigratorStateArgs,
  type InitConfig,
  type InitConfigArgs,
  type Launch,
  type LaunchArgs,
} from '../accounts';
import {
  getCreateSpotPoolInstructionAsync,
  getMigrateInstructionAsync,
  getRegisterLaunchInstructionAsync,
  parseCreateSpotPoolInstruction,
  parseMigrateInstruction,
  parseRegisterLaunchInstruction,
  type CreateSpotPoolAsyncInput,
  type MigrateAsyncInput,
  type ParsedCreateSpotPoolInstruction,
  type ParsedMigrateInstruction,
  type ParsedRegisterLaunchInstruction,
  type RegisterLaunchAsyncInput,
} from '../instructions';

export const CPMM_MIGRATOR_PROGRAM_ADDRESS =
  '7WMUTNC41eMCo6eGH5Sy2xbgE3AycvLbFPo95AU9CSUd' as Address<'7WMUTNC41eMCo6eGH5Sy2xbgE3AycvLbFPo95AU9CSUd'>;

export enum CpmmMigratorAccount {
  AmmConfig,
  CpmmMigratorState,
  InitConfig,
  Launch,
}

export function identifyCpmmMigratorAccount(
  account: { data: ReadonlyUint8Array } | ReadonlyUint8Array,
): CpmmMigratorAccount {
  const data = 'data' in account ? account.data : account;
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([218, 244, 33, 104, 203, 203, 43, 111]),
      ),
      0,
    )
  ) {
    return CpmmMigratorAccount.AmmConfig;
  }
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([169, 86, 255, 187, 37, 248, 11, 176]),
      ),
      0,
    )
  ) {
    return CpmmMigratorAccount.CpmmMigratorState;
  }
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([97, 166, 35, 7, 20, 2, 164, 126]),
      ),
      0,
    )
  ) {
    return CpmmMigratorAccount.InitConfig;
  }
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([144, 51, 51, 163, 206, 85, 213, 38]),
      ),
      0,
    )
  ) {
    return CpmmMigratorAccount.Launch;
  }
  throw new SolanaError(
    SOLANA_ERROR__PROGRAM_CLIENTS__FAILED_TO_IDENTIFY_ACCOUNT,
    { accountData: data, programName: 'cpmmMigrator' },
  );
}

export enum CpmmMigratorInstruction {
  CreateSpotPool,
  Migrate,
  RegisterLaunch,
}

export function identifyCpmmMigratorInstruction(
  instruction: { data: ReadonlyUint8Array } | ReadonlyUint8Array,
): CpmmMigratorInstruction {
  const data = 'data' in instruction ? instruction.data : instruction;
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([13, 137, 16, 26, 40, 36, 110, 26]),
      ),
      0,
    )
  ) {
    return CpmmMigratorInstruction.CreateSpotPool;
  }
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([155, 234, 231, 146, 236, 158, 162, 30]),
      ),
      0,
    )
  ) {
    return CpmmMigratorInstruction.Migrate;
  }
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([114, 114, 67, 23, 41, 70, 0, 225]),
      ),
      0,
    )
  ) {
    return CpmmMigratorInstruction.RegisterLaunch;
  }
  throw new SolanaError(
    SOLANA_ERROR__PROGRAM_CLIENTS__FAILED_TO_IDENTIFY_INSTRUCTION,
    { instructionData: data, programName: 'cpmmMigrator' },
  );
}

export type ParsedCpmmMigratorInstruction<
  TProgram extends string = '7WMUTNC41eMCo6eGH5Sy2xbgE3AycvLbFPo95AU9CSUd',
> =
  | ({
      instructionType: CpmmMigratorInstruction.CreateSpotPool;
    } & ParsedCreateSpotPoolInstruction<TProgram>)
  | ({
      instructionType: CpmmMigratorInstruction.Migrate;
    } & ParsedMigrateInstruction<TProgram>)
  | ({
      instructionType: CpmmMigratorInstruction.RegisterLaunch;
    } & ParsedRegisterLaunchInstruction<TProgram>);

export function parseCpmmMigratorInstruction<TProgram extends string>(
  instruction: Instruction<TProgram> & InstructionWithData<ReadonlyUint8Array>,
): ParsedCpmmMigratorInstruction<TProgram> {
  const instructionType = identifyCpmmMigratorInstruction(instruction);
  switch (instructionType) {
    case CpmmMigratorInstruction.CreateSpotPool: {
      assertIsInstructionWithAccounts(instruction);
      return {
        instructionType: CpmmMigratorInstruction.CreateSpotPool,
        ...parseCreateSpotPoolInstruction(instruction),
      };
    }
    case CpmmMigratorInstruction.Migrate: {
      assertIsInstructionWithAccounts(instruction);
      return {
        instructionType: CpmmMigratorInstruction.Migrate,
        ...parseMigrateInstruction(instruction),
      };
    }
    case CpmmMigratorInstruction.RegisterLaunch: {
      assertIsInstructionWithAccounts(instruction);
      return {
        instructionType: CpmmMigratorInstruction.RegisterLaunch,
        ...parseRegisterLaunchInstruction(instruction),
      };
    }
    default:
      throw new SolanaError(
        SOLANA_ERROR__PROGRAM_CLIENTS__UNRECOGNIZED_INSTRUCTION_TYPE,
        {
          instructionType: instructionType as string,
          programName: 'cpmmMigrator',
        },
      );
  }
}

export type CpmmMigratorPlugin = {
  accounts: CpmmMigratorPluginAccounts;
  instructions: CpmmMigratorPluginInstructions;
};

export type CpmmMigratorPluginAccounts = {
  ammConfig: ReturnType<typeof getAmmConfigCodec> &
    SelfFetchFunctions<AmmConfigArgs, AmmConfig>;
  cpmmMigratorState: ReturnType<typeof getCpmmMigratorStateCodec> &
    SelfFetchFunctions<CpmmMigratorStateArgs, CpmmMigratorState>;
  initConfig: ReturnType<typeof getInitConfigCodec> &
    SelfFetchFunctions<InitConfigArgs, InitConfig>;
  launch: ReturnType<typeof getLaunchCodec> &
    SelfFetchFunctions<LaunchArgs, Launch>;
};

export type CpmmMigratorPluginInstructions = {
  createSpotPool: (
    input: MakeOptional<CreateSpotPoolAsyncInput, 'payer'>,
  ) => ReturnType<typeof getCreateSpotPoolInstructionAsync> &
    SelfPlanAndSendFunctions;
  migrate: (
    input: MakeOptional<MigrateAsyncInput, 'payer'>,
  ) => ReturnType<typeof getMigrateInstructionAsync> & SelfPlanAndSendFunctions;
  registerLaunch: (
    input: MakeOptional<RegisterLaunchAsyncInput, 'payer'>,
  ) => ReturnType<typeof getRegisterLaunchInstructionAsync> &
    SelfPlanAndSendFunctions;
};

export type CpmmMigratorPluginRequirements = ClientWithRpc<
  GetAccountInfoApi & GetMultipleAccountsApi
> &
  ClientWithPayer &
  ClientWithTransactionPlanning &
  ClientWithTransactionSending;

export function cpmmMigratorProgram() {
  return <T extends CpmmMigratorPluginRequirements>(client: T) => {
    return {
      ...client,
      cpmmMigrator: <CpmmMigratorPlugin>{
        accounts: {
          ammConfig: addSelfFetchFunctions(client, getAmmConfigCodec()),
          cpmmMigratorState: addSelfFetchFunctions(
            client,
            getCpmmMigratorStateCodec(),
          ),
          initConfig: addSelfFetchFunctions(client, getInitConfigCodec()),
          launch: addSelfFetchFunctions(client, getLaunchCodec()),
        },
        instructions: {
          createSpotPool: (input) =>
            addSelfPlanAndSendFunctions(
              client,
              getCreateSpotPoolInstructionAsync({
                ...input,
                payer: input.payer ?? client.payer,
              }),
            ),
          migrate: (input) =>
            addSelfPlanAndSendFunctions(
              client,
              getMigrateInstructionAsync({
                ...input,
                payer: input.payer ?? client.payer,
              }),
            ),
          registerLaunch: (input) =>
            addSelfPlanAndSendFunctions(
              client,
              getRegisterLaunchInstructionAsync({
                ...input,
                payer: input.payer ?? client.payer,
              }),
            ),
        },
      },
    };
  };
}

type MakeOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
